import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  ImageContainer,
  ProductImage,
  EditImageButton,
  EditImageIcon,
  StyledModal,
  ModalContent,
  Title,
  CloseModalButton,
  CloseModalIcon,
  SectionColumn,
  UploadImageButton,
  ImageUrlTextbox,
  InputAdornment,
  SubmitUrlButton,
  Spinner,
  ModalDivider,
  ImageErrorSnackbar,
  Alert,
  RemoveImageButton,
  RemoveImageIcon,
  RemoveImageHelperText,
  BackdropStyles
} from './upload-image-modal.styles';
import apiClient from '../../../api/api-client';
import { UploadProductImageError } from '../../../errors/upload-product-image.error';
import noProductImage from '../../../images/no-product-image.png';
import { isProductImageExists } from '../../../utils/productImage';

interface UploadImageModalProps {
  mainImage: string;
  defaultImage: string;
  fallbackSrc?: string;
  onImageLoad: (initialLoad: boolean) => void;
  onImageChange: (newImageUrl?: string) => void;
}

interface RemoveImageDisplayState {
  isButtonDisabled: boolean;
  message: string;
}

export const UploadImageModal: FC<UploadImageModalProps> = ({
  mainImage,
  defaultImage,
  fallbackSrc,
  onImageLoad,
  onImageChange
}) => {
  const [isDefaultImageExists, setIsDefaultImageExists] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(true);
  const [isInitialImageLoad, setIsInitialImageLoad] = useState<boolean>(true);
  const [imageError, setImageError] = useState<UploadProductImageError | null>(null);
  const [removeImageDisplayData, setRemoveImageDisplayData] = useState<RemoveImageDisplayState>(
    {} as RemoveImageDisplayState
  );

  useEffect(() => {
    async function checkIsDefaultImageExists() {
      const response: boolean = await isProductImageExists(defaultImage);
      setIsDefaultImageExists(response);
    }
    checkIsDefaultImageExists();
  }, [defaultImage]);

  useEffect(() => {
    // If we're currently rendering the last-uploaded image then don't change the appearance of the remove image functionality
    if (isLoadingImage) {
      return;
    }

    let isButtonDisabled: boolean;
    let message: string;
    if (mainImage) {
      isButtonDisabled = false;
      message = isDefaultImageExists
        ? 'By removing the current image, a default product image will be automatically displayed for this product'
        : '';
    } else {
      isButtonDisabled = true;
      message = isDefaultImageExists
        ? 'This image is a default product image and cannot be removed. Try uploading a new image instead'
        : 'This product has no image';
    }
    setRemoveImageDisplayData({ isButtonDisabled, message });
  }, [isDefaultImageExists, mainImage, isLoadingImage]);

  function handleMouseOver() {
    setIsHovering(!isEditImageModalOpen);
  }

  function hideHoverEditIcon() {
    setIsHovering(false);
  }

  function openEditImageModal() {
    setIsEditImageModalOpen(true);
  }

  function closeEditImageModal() {
    setIsEditImageModalOpen(false);
    setImageUrl('');
    setImageError(null);
  }

  function handleChangeImageUrl(event: ChangeEvent<HTMLInputElement>) {
    setImageUrl(event.target.value);
    setImageError(null);
  }

  async function handleGetImageFromUrl() {
    if (!imageUrl) return;

    setIsLoadingImage(true);

    try {
      const uploadedImageUrl: string = await apiClient.uploadProductImageByUrl(imageUrl);
      onImageChange(uploadedImageUrl);
    } catch (error: UploadProductImageError | any) {
      handleImageError(error);
      setIsLoadingImage(false);
    }
  }

  async function handleGetImageFromFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData: FormData = new FormData();
    formData.append('image', file);

    setIsLoadingImage(true);

    try {
      const uploadedImageUrl: string = await apiClient.uploadProductImageByFile(formData);
      onImageChange(uploadedImageUrl);
    } catch (error) {
      handleImageError(error);
      setIsLoadingImage(false);
    }
  }

  function handleImageError(error: UploadProductImageError | any) {
    if (!(error instanceof UploadProductImageError)) {
      setImageError(new UploadProductImageError('Unknown image error', error.status, 'OTHER'));
      return;
    }

    let errorMessage: string;

    switch (error.type) {
      case 'DIMENSIONS-SMALL': {
        const minHeight: number = error?.args?.minHeight as number;
        const minWidth: number = error?.args?.minWidth as number;

        errorMessage = `Image is too small - minimum dimensions are: height - ${minHeight}, width - ${minWidth}`;
        break;
      }
      case 'SIZE-LARGE': {
        const maxSizeInMegabytes: number = error?.args?.maxSizeInMegabytes as number;

        errorMessage = `Image file is too large - maximum size is ${maxSizeInMegabytes}MB`;
        break;
      }
      case 'URL': {
        errorMessage = "URL doesn't exist";
        break;
      }
      case 'FORMAT': {
        errorMessage = 'Invalid image file';
        break;
      }
      default: {
        errorMessage = 'Unknown image error';
      }
    }

    setImageError(new UploadProductImageError(errorMessage, error.status, error.type));
  }

  function handleImageRemove() {
    setIsLoadingImage(true);
    closeEditImageModal();
    onImageChange();
  }

  function handleImageLoad() {
    setIsLoadingImage(false);
    closeEditImageModal();
    onImageLoad(isInitialImageLoad);

    if (isInitialImageLoad) {
      setIsInitialImageLoad(false);
    }
  }

  return (
    <ImageContainer onMouseLeave={hideHoverEditIcon} id='upload-image-modal-container'>
      {/* Showing the spinner up until the product image has finished loading completely */}
      <div style={{ display: isLoadingImage ? 'block' : 'none' }}>
        <Spinner id='upload-image-modal-spinner' />
      </div>

      <ProductImage
        src={mainImage || defaultImage}
        onError={(e) => (e.currentTarget.src = fallbackSrc || noProductImage)}
        onMouseOver={handleMouseOver}
        id='upload-image-modal-image'
        onLoad={handleImageLoad}
        sx={{ display: isLoadingImage ? 'none' : 'block' }}
      />
      {isHovering && (
        <EditImageButton
          disableRipple
          size='small'
          onClick={openEditImageModal}
          id='upload-image-modal-edit-button'
        >
          <EditImageIcon id='upload-image-modal-edit-icon' />
        </EditImageButton>
      )}
      <StyledModal
        open={isEditImageModalOpen}
        onClose={closeEditImageModal}
        id='upload-image-modal-body'
        sx={{ height: removeImageDisplayData.message ? '420px' : '370px' }}
        BackdropProps={BackdropStyles}
      >
        <ModalContent>
          {/* Displaing an appropriate error message if the input image is invalid and the error type isn't URL-related */}
          <ImageErrorSnackbar
            id='upload-image-modal-image-error-notification'
            open={imageError !== null && imageError.type !== 'URL'}
            autoHideDuration={10000}
            onClose={() => setImageError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity='error' onClose={() => setImageError(null)}>
              {imageError?.message}
            </Alert>
          </ImageErrorSnackbar>

          <CloseModalButton onClick={closeEditImageModal} id='upload-image-modal-close-button'>
            <CloseModalIcon />
          </CloseModalButton>
          <Title variant='h6'>Edit image</Title>
          <SectionColumn>
            <ImageUrlTextbox
              id='upload-image-modal-image-url-input'
              placeholder='Paste image URL here'
              label='Add image via URL'
              onChange={handleChangeImageUrl}
              error={imageError?.type === 'URL'}
              helperText={imageError?.type === 'URL' ? imageError.message : ' '}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                notched: false,
                endAdornment: (
                  <InputAdornment position='end'>
                    <SubmitUrlButton
                      disableRipple
                      onClick={handleGetImageFromUrl}
                      disabled={!imageUrl}
                      id='upload-image-modal-submit-url-button'
                    >
                      Attach
                    </SubmitUrlButton>
                  </InputAdornment>
                )
              }}
            />
            <span> OR </span>
            <UploadImageButton id='upload-image-modal-upload-image-button'>
              Upload image
              <input
                type='file'
                accept='image/*'
                hidden
                value=''
                onChange={handleGetImageFromFile}
                id='upload-image-modal-select-image-input'
              />
            </UploadImageButton>

            <ModalDivider />
            <RemoveImageButton
              disabled={removeImageDisplayData.isButtonDisabled}
              onClick={handleImageRemove}
              disableRipple
              startIcon={<RemoveImageIcon />}
              id='upload-image-modal-remove-image-button'
            >
              Remove Image
            </RemoveImageButton>
            <RemoveImageHelperText id='upload-image-modal-remove-image-text'>
              {removeImageDisplayData.message}
            </RemoveImageHelperText>
          </SectionColumn>
        </ModalContent>
      </StyledModal>
    </ImageContainer>
  );
};
